/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {academicGradingSystem, generalSettingsService} from "services"
import {handleError} from "helpers"
import styles from "./GradingSystem.module.css"
import {BaseNewFilter, KlassappTable, KlassappTableHeader} from "uiKit"
import {useTranslation} from "react-i18next"
import {useHistory} from "react-router-dom"
import {KlassappTableHOC} from "HOC"
import {BaseButton} from "components"
import {debounce} from "lodash"
import {Checkbox, Radio} from "antd"
import {FameGradeExportPracticalType, GradingType, useGradingSystemSetting} from "hooks/useGradingSystemSetting"
import {useMutation} from "@tanstack/react-query"
import {GeneralSettingField} from "types/settings/general-setting"
import {YesNo} from "types/common"

function GradingSystem(props) {
    const [filter, setFilter] = useState({
        campus: null,
        instructor: null
    })

    const [gradingSystemSettings, setGradingSystemSettings] = useState({
        courseGrades: false,
        practical: false,
        classActivities: false,
        practicalType: FameGradeExportPracticalType.Individual,
        gradingType: GradingType.BasedOnGradingSystem
    })

    const settings = useGradingSystemSetting()

    useEffect(() => {
        setGradingSystemSettings({
            courseGrades: settings.isCheckedFameGradeExportCourseGrades,
            practical: settings.isCheckedFameGradeExportPractical,
            classActivities: settings.isCheckedFameGradeExportClassActivities,
            practicalType: settings.fameGradeExportPracticalType,
            gradingType: settings.gradingType
        })
    }, [
        settings.isCheckedFameGradeExportClassActivities,
        settings.isCheckedFameGradeExportCourseGrades,
        settings.isCheckedFameGradeExportPractical,
        settings.fameGradeExportPracticalType,
        settings.gradingType
    ])

    const updateGeneralSettings = useMutation(
        async (payload: Array<{field: GeneralSettingField; checked: boolean | string}>) => {
            await generalSettingsService.updateSettings(
                payload.map(({field, checked}) => {
                    if (typeof checked === "boolean") {
                        return {
                            field,
                            value: checked ? YesNo.Yes : YesNo.No
                        }
                    }
                    return {
                        field,
                        value: checked
                    }
                })
            )
        },
        {
            onSuccess() {
                settings.refetch()
            },
            onError: (err) => {
                handleError(err)
            }
        }
    )

    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        getCurrentData,
        isHideMenuActions,
        menuActions,
        data,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        isLoading,
        isShowTableHeaderAction,
        departmentId
    } = props

    const textSearch = useRef<string>()

    const searchGradingSystem = async (text: string) => {
        textSearch.current = text
        dispatch({isLoading: true})
        try {
            const params = getParams()
            const {data, total} = await academicGradingSystem.getListGradingSystem(params)
            dispatch({data: data, total: total})
        } catch (e) {
            console.log("e")
        } finally {
            dispatch({isLoading: false})
        }
    }

    const debouncedChangeHandler = useMemo(() => debounce(searchGradingSystem, 600), [])

    // Stop the invocation of the debounced function after unmounting
    useEffect(() => {
        return () => {
            debouncedChangeHandler.cancel()
        }
    }, [debouncedChangeHandler])

    const {t} = useTranslation(["course"])
    const history = useHistory()

    const getFields = () => {
        return ["ID", t("academics:settings.description")]
    }

    const getPageTitle = () => {
        return "Course"
    }

    const getTableHeaderActions = () => {
        const {t, onClickShowConfirmModal} = props
        return [
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            }
        ]
    }

    const getColumns = () => {
        return [
            {
                title: "ID",
                field: "code",
                style: {minWidth: "70px"},
                headerStyle: {minWidth: "70px"}
            },
            {
                title: t("academics:settings.description"),
                field: "title",
                style: {minWidth: "150px"},
                headerStyle: {minWidth: "150px"}
            }
        ]
    }

    const getParams = () => {
        const params: any = {
            range: {
                page,
                pageSize
            },
            filter: {
                text: textSearch.current
            }
        }
        return params
    }

    const getData = async () => {
        dispatch({isLoading: true})
        try {
            const params = getParams()
            const {data, total} = await academicGradingSystem.getListGradingSystem(params)
            dispatch({data: data, total: total})
        } catch (e) {
            console.log("e")
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClickRowItem = (row) => {
        history.push(`/settings/add-grading?id=${row.id}`)
    }

    const onClickDeleteMulti = useCallback(async () => {
        const checkedItems = getCurrentData().filter((item) => item.isChecked)
        const payload = checkedItems.map((item) => item.id)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await academicGradingSystem.deleteGradingSystem(payload)
            await getData()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }, [data])

    const onClickEdit = (editItem) => {
        history.push(`/settings/add-grading?id=${editItem.id}`)
    }

    const onClickDelete = useCallback(async (deletedItem) => {
        try {
            dispatch({isLoading: true})
            await academicGradingSystem.deleteGradingSystem([deletedItem.id])
            await getData()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }, [])

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
    }, [data])

    useEffect(() => {
        getData()
    }, [page, pageSize])

    useEffect(() => {
        props.updateTableHeaderActions()
    }, [data])

    const getMenuActions = () => {
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: onClickEdit
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: onClickDelete
            }
        ]
    }

    const onClickAdd = useCallback(() => {
        history.push(`/settings/add-grading`)
    }, [])

    const onClickClearFilter = useCallback(() => {}, [])

    const onApplyFilter = useCallback(() => {}, [])

    const onSearchInput = useCallback((input: string) => {
        debouncedChangeHandler(input)
    }, [])

    return (
        <div className={styles.wrapper}>
            <span className={styles.title}>Fame Grades Export</span>
            <div className={styles.checkboxContainer}>
                <Checkbox
                    checked={gradingSystemSettings.courseGrades}
                    onChange={(event) => {
                        updateGeneralSettings.mutate([
                            {
                                field: GeneralSettingField.FameGradeExportCourseGrades,
                                checked: event.target.checked
                            }
                        ])
                    }}>
                    Course Grades
                </Checkbox>

                <Checkbox
                    checked={gradingSystemSettings.classActivities}
                    onChange={(event) =>
                        updateGeneralSettings.mutate([
                            {
                                field: GeneralSettingField.FameGradeExportClassActivities,
                                checked: event.target.checked
                            }
                        ])
                    }>
                    Class Activities
                </Checkbox>

                <div className={styles.practicalContainer}>
                    <Checkbox
                        checked={gradingSystemSettings.practical}
                        onChange={(event) =>
                            updateGeneralSettings.mutate([
                                {
                                    field: GeneralSettingField.FameGradeExportPractical,
                                    checked: event.target.checked
                                }
                            ])
                        }>
                        Practical
                    </Checkbox>
                    {gradingSystemSettings.practical ? (
                        <Radio.Group
                            value={gradingSystemSettings.practicalType}
                            onChange={(event) => {
                                const value = event.target.value
                                updateGeneralSettings.mutate([
                                    {
                                        field: GeneralSettingField.FameGradeExportPracticalType,
                                        checked: value
                                    }
                                ])
                            }}>
                            <Radio
                                value={FameGradeExportPracticalType.ModulePercentage}
                                className={styles.practicalType}>
                                Module Percentage
                            </Radio>
                            <Radio
                                value={FameGradeExportPracticalType.SubmodulePercentage}
                                className={styles.practicalType}>
                                Submodule Percentage
                            </Radio>
                            <Radio value={FameGradeExportPracticalType.Individual} className={styles.practicalType}>
                                Individual
                            </Radio>
                        </Radio.Group>
                    ) : null}
                </div>
            </div>

            <span className={styles.title}>Grading Type</span>
            <div className={styles.checkboxContainer}>
                <Radio.Group
                    value={gradingSystemSettings.gradingType}
                    onChange={(event) => {
                        const value = event.target.value
                        updateGeneralSettings.mutate([
                            {
                                field: GeneralSettingField.GradingType,
                                checked: value
                            }
                        ])
                    }}>
                    <Radio value={GradingType.BasedOnGradingSystem} className={styles.practicalType}>
                        Based On Grading System
                    </Radio>
                    <Radio value={GradingType.ClassActivityHighestGrade} className={styles.practicalType}>
                        Class Activity Highest Grade
                    </Radio>
                </Radio.Group>
            </div>

            <BaseNewFilter
                className={styles.filterWrap}
                filter={filter}
                onClick={onApplyFilter}
                onClickClear={onClickClearFilter}
                onSearchInput={onSearchInput}
            />

            <div className={styles.action}>
                <BaseButton title="Add" variant="primary" onClick={onClickAdd} />
            </div>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                menuActions={isHideMenuActions ? [] : menuActions}
                isLoading={isLoading}
                allFields={allFields}
                isShowCheckedColumn
                orderField={orderField}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
        </div>
    )
}

export default KlassappTableHOC(GradingSystem)
